import { inject, Injectable } from '@angular/core';
import {
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { ImpersonationActions } from '@ps/auth-data-access';
import { SURVEY_STATUS_LABELS } from '@ps/survey-kernel';
import { markCached, PSError } from '@ps/utils';

import { SurveyService } from '../../services/survey/survey.service';
import {
  isChildSurveysCacheValid,
  SURVEY_SLOW_LOAD_THRESHOLD_MS,
} from '../../utils';

import { SurveyListActions, SurveyListApiActions } from './survey-list.actions';
import {
  selectLoadingSnapshot,
  selectChildSurveysCache,
  selectFilters,
  selectQueryParams,
} from './survey-list.selectors';
import { SurveyCancelStrategy } from '../survey-state.model';

@Injectable()
export class SurveyEffects extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _surveyService = inject(SurveyService);
  private readonly _toastService = inject(ToastService);

  public loadEntities$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyListActions.setFilters,
        SurveyListActions.setSort,
        SurveyListActions.loadNextPage,
        SurveyListActions.reload,
        SurveyListActions.setTab,
        SurveyListActions.cancelLoad,
        SurveyListApiActions.loadTimeout,
      ),
      concatLatestFrom(() => [
        this._store.select(selectQueryParams),
        this._store.select(selectLoadingSnapshot),
      ]),
      switchMap(([action, params, loadingSnapshot]) => {
        if (this._shouldSkipLoad(action, loadingSnapshot)) {
          return EMPTY;
        }

        return this._surveyService.getAll(params).pipe(
          map((surveys) => SurveyListApiActions.loadSuccess({ surveys })),
          catchError((error) => {
            if (
              error instanceof PSError &&
              error.metadata['kind'] === 'timeout'
            ) {
              this._toastService.error(error.message);
              return of(SurveyListApiActions.loadTimeout());
            }

            return throwError(() => error);
          }),
          this.psCatchError(
            (err) =>
              of(
                SurveyListApiActions.loadFailure({
                  error: err.message || 'An unknown error occurred',
                }),
              ),
            { notify: true },
          ),
          // Leaving the dashboard resets the list to empty; a late loadSuccess
          // would repopulate it and make the socket room re-subscribe after teardown.
          takeUntil(this._actions$.pipe(ofType(SurveyListActions.teardown))),
        );
      }),
    ),
  );

  public loadCounts$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyListActions.init,
        SurveyListActions.setFilters,
        SurveyListActions.reload,
        SurveyListActions.cancelLoad,
        SurveyListApiActions.loadTimeout,
      ),
      concatLatestFrom(() => [
        this._store.select(selectQueryParams),
        this._store.select(selectLoadingSnapshot),
      ]),
      switchMap(([action, params, loadingSnapshot]) => {
        if (this._shouldSkipLoad(action, loadingSnapshot)) {
          return EMPTY;
        }

        return this._surveyService.getCounts(params).pipe(
          map((counts) => SurveyListApiActions.loadCountsSuccess({ counts })),
          this.psCatchError(() =>
            of(SurveyListApiActions.loadCountsSuccess({ counts: {} })),
          ),
        );
      }),
    ),
  );

  public slowLoadingTimer$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyListActions.setFilters,
        SurveyListActions.setSort,
        SurveyListActions.setTab,
        SurveyListActions.loadNextPage,
        SurveyListActions.cancelLoad,
        SurveyListApiActions.loadTimeout,
        SurveyListActions.reload,
      ),
      concatLatestFrom(() => this._store.select(selectLoadingSnapshot)),
      filter(([action, snapshot]) => !this._shouldSkipLoad(action, snapshot)),
      switchMap(() =>
        timer(SURVEY_SLOW_LOAD_THRESHOLD_MS).pipe(
          takeUntil(
            this._actions$.pipe(
              ofType(
                SurveyListApiActions.loadSuccess,
                SurveyListApiActions.loadFailure,
                SurveyListApiActions.loadTimeout,
                SurveyListActions.cancelLoad,
              ),
            ),
          ),
          map(() => SurveyListApiActions.slowLoadingDetected()),
        ),
      ),
    ),
  );

  /**
   * The reducer already merges `hasImpersonatedBuyer` into `context` when it
   * handles this same action, so a reload is enough.
   */
  public reloadOnImpersonationContextSettled$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ImpersonationActions.contextSettled),
      map(() => SurveyListActions.reload()),
    ),
  );

  public loadChildSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyListActions.loadChildSurveys),
      concatLatestFrom(() => this._store.select(selectChildSurveysCache)),
      filter(
        ([{ parentId }, { childSurveys, cachedAt }]) =>
          !isChildSurveysCacheValid(parentId, childSurveys, cachedAt),
      ),
      switchMap(([{ parentId, filter: childFilter }]) => {
        const call$ =
          'tcParentId' in childFilter
            ? this._surveyService.getTrafficChannelChildren(
                childFilter.tcParentId,
              )
            : this._surveyService.getChildSurveys(parentId, childFilter);
        return call$.pipe(
          map((surveys) =>
            SurveyListApiActions.loadChildSurveysSuccess({
              parentId,
              surveys,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError(() =>
            of(
              SurveyListApiActions.loadChildSurveysFailure({
                parentId,
                error: 'Failed to load child surveys',
              }),
            ),
          ),
        );
      }),
    ),
  );

  public updateSurveyStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyListActions.updateSurveyStatus),
      concatLatestFrom(() => this._store.select(selectFilters)),
      mergeMap(([{ surveyId, status }, filters]) =>
        this._surveyService.updateSurveyStatus(surveyId, status).pipe(
          map(() =>
            SurveyListApiActions.updateSurveyStatusSuccess({
              surveyId,
              status,
              statusFilter: filters.status,
            }),
          ),
          this.psCatchError((err) =>
            of(
              SurveyListApiActions.updateSurveyStatusFailure({
                surveyId,
                error: err.message || 'Failed to update survey status',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateSurveyStatusSuccessToast$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyListApiActions.updateSurveyStatusSuccess),
        tap(({ status }) => {
          const label = SURVEY_STATUS_LABELS[status];
          this._toastService.success(`Survey status changed to ${label}.`, {
            keepOpen: false,
          });
        }),
      ),
    { dispatch: false },
  );

  public updateSurveyStatusFailureToast$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyListApiActions.updateSurveyStatusFailure),
        tap(({ error }) => {
          this._toastService.error(error);
        }),
      ),
    { dispatch: false },
  );

  private _shouldSkipLoad(
    action: Action,
    loadingSnapshot: SurveyCancelStrategy | null,
  ): boolean {
    const isCancelOrTimeout =
      action.type === SurveyListActions.cancelLoad.type ||
      action.type === SurveyListApiActions.loadTimeout.type;

    return isCancelOrTimeout && loadingSnapshot?.kind !== 'refetch';
  }
}
